import { createURL } from '@/domains/core/routing/URLParser';
import { isB2C, isFrancePlatform } from '@/domains/core/settings/utils';
import {
  MAX_NB_PAGES,
  NB_PRODUCTS_PER_PAGE,
  NB_PRODUCTS_PER_PAGE_ALTERNATE,
} from '@/productDiscovery/Listings/constants';
import {
  decodePlus,
  stringify,
} from '@/productDiscovery/Listings/services/querystring';
import { getPageInfo } from '@/productDiscovery/Listings/services/route';

import { BLACKLISTED_PARAMS_SEO_PAGINATION } from './constants';

const generateSeoUrl = (url: string, page: number) =>
  decodePlus(page === 1 ? url : `${url}?page=${page}`);

const numberOfPage = (
  total: number,
  productsPerPage = NB_PRODUCTS_PER_PAGE,
): number => Math.ceil(total / productsPerPage);

export const getSeoPaginationLink = (
  url: string,
  currentPage: number,
  totalProducts: number,
  productsPerPage = NB_PRODUCTS_PER_PAGE,
): { nextLink?: string; prevLink?: string } => {
  const totalPage = numberOfPage(totalProducts, productsPerPage);

  if (currentPage === 1 && totalPage === 1) {
    return {
      nextLink: undefined,
      prevLink: undefined,
    };
  }

  if (currentPage === 1 && totalPage > 1) {
    return {
      nextLink: generateSeoUrl(url, currentPage + 1),
    };
  }

  if (currentPage > 1 && totalPage > currentPage) {
    return {
      nextLink: generateSeoUrl(url, currentPage + 1),
      prevLink: generateSeoUrl(url, currentPage - 1),
    };
  }
  if (totalPage === currentPage) {
    return {
      prevLink: generateSeoUrl(url, currentPage - 1),
    };
  }
  return {
    nextLink: undefined,
    prevLink: undefined,
  };
};

export const generatePaginationUrl = (asPath: string, page: number): string => {
  const pageInfo = getPageInfo(asPath);
  if (!pageInfo) return '';

  if (page > 1) {
    BLACKLISTED_PARAMS_SEO_PAGINATION.forEach((param) => {
      delete pageInfo.queryParams?.[param];
    });
  }

  let baseUrl = createURL(
    pageInfo.urlPattern,
    pageInfo.pathParams as Record<string, string>,
  );
  baseUrl = decodePlus(baseUrl);

  return `${baseUrl}${stringify({ ...pageInfo.queryParams, page: page > 1 ? page : undefined })}`;
};

export const getProductsPerPage = (pageName?: string | null): number =>
  isFrancePlatform() && isB2C() && pageName === 'category'
    ? NB_PRODUCTS_PER_PAGE_ALTERNATE
    : NB_PRODUCTS_PER_PAGE;

export const getTotalPages = (
  productsPerPage: number,
  nbProducts?: number,
): number => {
  if (!nbProducts) return 1;
  const totalItems =
    nbProducts / productsPerPage > MAX_NB_PAGES
      ? MAX_NB_PAGES * productsPerPage
      : nbProducts;
  return Math.ceil(totalItems / productsPerPage);
};
